import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const resultsHtml = `
    <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">

    <div class="container">
        <!-- Header -->
        <div class="header">
            <h1>Swing Trading Analysis Report</h1>
            <div class="subtitle">Weekly Options with AI Bias: Can It Beat 0DTE?</div>
            <div class="subtitle" style="opacity: 0.7; font-size: 0.9rem; margin-top: 15px;">
                Analysis Period: January - December 2025 | AI Model: Claude Opus 4.5
            </div>
        </div>

        <!-- Summary -->
        <div class="section">
            <h2>Summary</h2>
            <p>
                This report analyzes whether the successful 0DTE AI bias strategy can be adapted for <strong>swing trading with weekly options</strong>.
                We tested holding positions through the week, exiting on bias reversals, and pyramiding (adding to winners).
            </p>

            <div class="metric-cards">
                <div class="metric-card success">
                    <div class="label">0DTE Strategy (Baseline)</div>
                    <div class="value">$34,626</div>
                </div>
                <div class="metric-card">
                    <div class="label">Weekly Swing (No Pyramid)</div>
                    <div class="value">$32,319</div>
                </div>
                <div class="metric-card danger">
                    <div class="label">Weekly + Pyramiding</div>
                    <div class="value">$8,153</div>
                </div>
            </div>

            <div class="success-box">
                <h5>Key Finding</h5>
                <p class="mb-0">
                    <strong>Weekly swing trading is viable</strong> and produces comparable returns to 0DTE ($32,319 vs $34,626).
                    However, <strong>pyramiding destroys returns</strong> due to theta decay acceleration - reducing profits by 75%.
                </p>
            </div>
        </div>

        <!-- Strategy Overview -->
        <div class="section">
            <h2>Strategy Comparison</h2>

            <h3>Swing Trading Rules</h3>
            <ul>
                <li><strong>Entry:</strong> Buy ATM weekly options (Friday expiration) based on daily AI bias signal</li>
                <li><strong>Exit on Reversal:</strong> When AI bias flips direction, close position and enter opposite</li>
                <li><strong>Friday Exit:</strong> Close all positions before expiration</li>
                <li><strong>Premium:</strong> ~1.8% of underlying (~$10 vs $3.30 for 0DTE)</li>
            </ul>

            <table class="comparison-table">
                <thead>
                    <tr>
                        <th>Metric</th>
                        <th>0DTE</th>
                        <th>Weekly Swing</th>
                        <th>Weekly + Pyramid</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="highlight-row">
                        <td><strong>Net P&L</strong></td>
                        <td class="positive"><strong>$34,626</strong> <span class="badge-best">BEST</span></td>
                        <td class="positive">$32,319</td>
                        <td class="negative">$8,153 <span class="badge-avoid">AVOID</span></td>
                    </tr>
                    <tr>
                        <td>ROI ($5k capital)</td>
                        <td class="positive">693%</td>
                        <td class="positive">646%</td>
                        <td>163%</td>
                    </tr>
                    <tr>
                        <td>Total Trades</td>
                        <td>102</td>
                        <td>61</td>
                        <td>61</td>
                    </tr>
                    <tr>
                        <td>Win Rate</td>
                        <td>69.6%</td>
                        <td>55.7%</td>
                        <td>39.3%</td>
                    </tr>
                    <tr>
                        <td>Average Win</td>
                        <td>$581</td>
                        <td>$1,204</td>
                        <td>$1,131</td>
                    </tr>
                    <tr>
                        <td>Average Loss</td>
                        <td>-$202</td>
                        <td>-$305</td>
                        <td>-$501</td>
                    </tr>
                    <tr>
                        <td>Average Premium</td>
                        <td>$330</td>
                        <td>$975</td>
                        <td>$975</td>
                    </tr>
                    <tr>
                        <td>Avg Days Held</td>
                        <td>1 day</td>
                        <td>3.9 days</td>
                        <td>3.9 days</td>
                    </tr>
                </tbody>
            </table>

            <div class="insight-box">
                <h5>Key Insight</h5>
                <p class="mb-0">
                    Weekly swing trades have a <strong>lower win rate (55.7% vs 69.6%)</strong> but <strong>bigger winners ($1,204 vs $581)</strong>.
                    With fewer trades (61 vs 102), it requires less daily management while producing similar returns.
                </p>
            </div>
        </div>

        <!-- Consecutive Days Analysis -->
        <div class="section">
            <h2>AI Bias Streak Analysis</h2>
            <p>To evaluate swing trading viability, we analyzed how often the AI bias maintains direction across consecutive days.</p>

            <h3>Consecutive Same-Direction Streaks</h3>
            <table>
                <thead>
                    <tr>
                        <th>Streak Length</th>
                        <th>Count</th>
                        <th>% of Streaks</th>
                        <th>Implication</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>1 day (flips next day)</td>
                        <td>22</td>
                        <td>48.9%</td>
                        <td class="negative">Quick reversal - challenging for swing</td>
                    </tr>
                    <tr>
                        <td>2 days</td>
                        <td>9</td>
                        <td>20.0%</td>
                        <td>Moderate - can capture some gains</td>
                    </tr>
                    <tr>
                        <td>3 days</td>
                        <td>7</td>
                        <td>15.6%</td>
                        <td class="positive">Good swing opportunity</td>
                    </tr>
                    <tr>
                        <td>4+ days</td>
                        <td>7</td>
                        <td>15.5%</td>
                        <td class="positive">Excellent trend capture</td>
                    </tr>
                </tbody>
            </table>

            <div class="metric-cards">
                <div class="metric-card">
                    <div class="label">Average Streak</div>
                    <div class="value">2.4 days</div>
                </div>
                <div class="metric-card success">
                    <div class="label">Days in 2+ Day Streaks</div>
                    <div class="value">79.6%</div>
                </div>
                <div class="metric-card">
                    <div class="label">Max Streak</div>
                    <div class="value">11 days</div>
                </div>
            </div>

            <h3>Weekly Consistency</h3>
            <table>
                <thead>
                    <tr>
                        <th>Week Type</th>
                        <th>Count</th>
                        <th>Percentage</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Fully Bullish (5/5 days)</td>
                        <td>4</td>
                        <td>21.1%</td>
                    </tr>
                    <tr>
                        <td>Fully Bearish (5/5 days)</td>
                        <td>2</td>
                        <td>10.5%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td>Mixed (2-3 each way)</td>
                        <td>13</td>
                        <td>68.4%</td>
                    </tr>
                </tbody>
            </table>

            <div class="warning-box">
                <h5>Warning: High Reversal Risk</h5>
                <p class="mb-0">
                    <strong>68.4% of weeks are mixed</strong> with mid-week bias reversals. This is why the "exit on reversal"
                    strategy is critical - holding through reversals would destroy profits.
                </p>
            </div>
        </div>

        <!-- Pyramiding Analysis -->
        <div class="section">
            <h2>Why Pyramiding Fails with Weekly Options</h2>

            <p>
                Pyramiding (adding to winning positions) is a classic strategy that works well for stocks and long-dated options.
                However, our backtest shows it <strong>reduces weekly options returns by 75%</strong>. Here's why:
            </p>

            <h3>The Theta Decay Death Spiral</h3>

            <div class="timeline">
                <div class="timeline-item profit">
                    <strong>Monday:</strong> Buy 1 ATM call at $10.00 (100% time value)
                </div>
                <div class="timeline-item profit">
                    <strong>Wednesday:</strong> Position worth $11.00 (+10%). Add 1 contract at $9.00
                    <br><small class="text-muted">Combined: $19.00 invested, 2 contracts</small>
                </div>
                <div class="timeline-item loss">
                    <strong>Thursday:</strong> Bias reverses, market back to even
                    <br>Contract 1: $5.00 (-50% from entry)
                    <br>Contract 2: $4.00 (-56% from entry - <strong>FASTER decay!</strong>)
                    <br><strong>Total: $9.00 value on $19.00 invested = -53% loss</strong>
                </div>
            </div>

            <div class="formula-box">
<strong>WITHOUT Pyramid:</strong> $5.00 loss on $10.00 = -50%
<strong>WITH Pyramid:</strong>    $10.00 loss on $19.00 = -53% (but 2x the dollars lost!)

The pyramid contract decays 65% FASTER because less time remains.
Wednesday theta: -$2/day on $6 time value = 33% daily decay
Monday theta:    -$2/day on $10 time value = 20% daily decay
            </div>

            <h3>The 10-to-1 Rule</h3>
            <p>Research shows pyramiding requires trends lasting <strong>10x longer than your holding period</strong>:</p>

            <table>
                <thead>
                    <tr>
                        <th>Option Type</th>
                        <th>Avg Hold</th>
                        <th>Trend Needed</th>
                        <th>Reality</th>
                        <th>Pyramid Viable?</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>Weekly (5 DTE)</strong></td>
                        <td>2-3 days</td>
                        <td>20-30 days</td>
                        <td>Trends last 5-10 days</td>
                        <td><span class="badge bg-danger">NO</span></td>
                    </tr>
                    <tr>
                        <td><strong>Monthly (30 DTE)</strong></td>
                        <td>10-15 days</td>
                        <td>100-150 days</td>
                        <td>Major trends: 60-120 days</td>
                        <td><span class="badge bg-warning text-dark">MAYBE</span></td>
                    </tr>
                    <tr>
                        <td><strong>Quarterly (60+ DTE)</strong></td>
                        <td>30-45 days</td>
                        <td>300+ days</td>
                        <td>Bull/bear markets: 1-3 years</td>
                        <td><span class="badge bg-success">YES</span></td>
                    </tr>
                    <tr>
                        <td><strong>LEAPS (180+ DTE)</strong></td>
                        <td>60-90 days</td>
                        <td>600+ days</td>
                        <td>Secular trends: 2-5 years</td>
                        <td><span class="badge bg-success">OPTIMAL</span></td>
                    </tr>
                </tbody>
            </table>

            <h3>The Math Formula</h3>
            <div class="formula-box">
Pyramiding Viability = (Trend Duration / Hold Time) x (1 - Daily Theta%)

<strong>Weekly:</strong>   (5 / 3) x (1 - 0.15) = 1.42  <span style="color: #f44336;">FAIL (need &gt;1.8)</span>
<strong>Monthly:</strong>  (30 / 15) x (1 - 0.04) = 1.92 <span style="color: #4caf50;">VIABLE</span>
<strong>LEAPS:</strong>    (180 / 60) x (1 - 0.01) = 2.97 <span style="color: #4caf50;">OPTIMAL</span>
            </div>

            <h3>The Paradox: 88.9% Win Rate, 75% Profit Reduction</h3>
            <p>Our pyramided trades won 88.9% of the time - so why did overall performance drop?</p>

            <table>
                <thead>
                    <tr>
                        <th>Trade Type</th>
                        <th>Count</th>
                        <th>P&L</th>
                        <th>Impact</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Single-contract trades</td>
                        <td>52</td>
                        <td class="negative">-$3,597</td>
                        <td>Quick reversals = losses</td>
                    </tr>
                    <tr>
                        <td>Pyramided trades (2 contracts)</td>
                        <td>9</td>
                        <td class="positive">+$12,212</td>
                        <td>Trends captured well</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>Net Result</strong></td>
                        <td>61</td>
                        <td>+$8,153</td>
                        <td>-75% vs no pyramid</td>
                    </tr>
                </tbody>
            </table>

            <div class="warning-box">
                <h5>The Asymmetric Loss Problem</h5>
                <p class="mb-0">
                    When you pyramid into a winner that reverses, you have <strong>2x the contracts losing money at accelerated theta decay</strong>.
                    The 11.1% of losing pyramid trades destroyed the gains from the 88.9% of winners.
                </p>
            </div>
        </div>

        <!-- Top Trades -->
        <div class="section">
            <h2>Sample Swing Trades</h2>

            <h3>Top 10 Winning Trades</h3>
            <table>
                <thead>
                    <tr>
                        <th>Entry</th>
                        <th>Exit</th>
                        <th>Direction</th>
                        <th>Days Held</th>
                        <th>P&L</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Feb 24</td>
                        <td>Mar 7</td>
                        <td>BEARISH</td>
                        <td>11</td>
                        <td class="positive">+$2,743</td>
                    </tr>
                    <tr>
                        <td>Apr 4</td>
                        <td>Apr 4</td>
                        <td>BEARISH</td>
                        <td>1</td>
                        <td class="positive">+$1,960</td>
                    </tr>
                    <tr>
                        <td>Mar 26</td>
                        <td>Mar 28</td>
                        <td>BEARISH</td>
                        <td>2</td>
                        <td class="positive">+$1,695</td>
                    </tr>
                    <tr>
                        <td>Mar 10</td>
                        <td>Mar 14</td>
                        <td>BEARISH</td>
                        <td>4</td>
                        <td class="positive">+$1,342</td>
                    </tr>
                    <tr>
                        <td>Oct 10</td>
                        <td>Oct 10</td>
                        <td>BEARISH</td>
                        <td>1</td>
                        <td class="positive">+$1,346</td>
                    </tr>
                    <tr>
                        <td>Jan 7</td>
                        <td>Jan 10</td>
                        <td>BEARISH</td>
                        <td>3</td>
                        <td class="positive">+$893</td>
                    </tr>
                    <tr>
                        <td>Jan 15</td>
                        <td>Jan 17</td>
                        <td>BULLISH</td>
                        <td>2</td>
                        <td class="positive">+$795</td>
                    </tr>
                    <tr>
                        <td>Feb 10</td>
                        <td>Feb 21</td>
                        <td>BULLISH</td>
                        <td>11</td>
                        <td class="positive">+$785</td>
                    </tr>
                    <tr>
                        <td>Mar 24</td>
                        <td>Mar 26</td>
                        <td>BULLISH</td>
                        <td>2</td>
                        <td class="positive">+$618</td>
                    </tr>
                    <tr>
                        <td>Feb 5</td>
                        <td>Feb 7</td>
                        <td>BULLISH</td>
                        <td>2</td>
                        <td class="positive">+$540</td>
                    </tr>
                </tbody>
            </table>

            <div class="insight-box">
                <h5>Pattern: Multi-Day Trends = Big Winners</h5>
                <p class="mb-0">
                    The biggest winners (Feb 24-Mar 7: +$2,743) came from extended trends lasting 11 days.
                    This is why the bias-reversal exit strategy works - it lets winners run while cutting losers.
                </p>
            </div>
        </div>

        <!-- When to Use Each Strategy -->
        <div class="section">
            <h2>Strategy Recommendations</h2>

            <h3>When to Use Each Approach</h3>
            <table>
                <thead>
                    <tr>
                        <th>Strategy</th>
                        <th>Best For</th>
                        <th>Pros</th>
                        <th>Cons</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>0DTE</strong></td>
                        <td>Daily traders who can monitor positions</td>
                        <td>
                            <ul class="mb-0">
                                <li>Higher win rate (69.6%)</li>
                                <li>Best overall returns</li>
                                <li>No overnight risk</li>
                            </ul>
                        </td>
                        <td>
                            <ul class="mb-0">
                                <li>Requires daily attention</li>
                                <li>102 trades/year</li>
                            </ul>
                        </td>
                    </tr>
                    <tr>
                        <td><strong>Weekly Swing</strong></td>
                        <td>Part-time traders who can't monitor daily</td>
                        <td>
                            <ul class="mb-0">
                                <li>Fewer trades (61/year)</li>
                                <li>Bigger winners ($1,204 avg)</li>
                                <li>Less screen time</li>
                            </ul>
                        </td>
                        <td>
                            <ul class="mb-0">
                                <li>Lower win rate (55.7%)</li>
                                <li>Overnight gap risk</li>
                                <li>Higher premium cost</li>
                            </ul>
                        </td>
                    </tr>
                    <tr>
                        <td><strong>Pyramiding</strong></td>
                        <td>Monthly+ options or stock positions</td>
                        <td>
                            <ul class="mb-0">
                                <li>Amplifies gains on long trends</li>
                                <li>Works with LEAPS/stocks</li>
                            </ul>
                        </td>
                        <td>
                            <ul class="mb-0">
                                <li>Does NOT work with weekly options</li>
                                <li>Theta decay destroys math</li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>

            <h3>Hybrid Approach</h3>
            <p>For maximum flexibility, consider running both strategies:</p>
            <ul>
                <li><strong>Primary:</strong> 0DTE for consistent daily returns ($34,626/year)</li>
                <li><strong>Secondary:</strong> Weekly swing on high-conviction multi-day setups ($32,319/year)</li>
                <li><strong>Combined potential:</strong> Diversification across timeframes</li>
            </ul>

            <div class="success-box">
                <h5>Final Recommendation</h5>
                <p class="mb-2"><strong>For Weekly Options:</strong></p>
                <ul class="mb-0">
                    <li>Use the bias-reversal exit strategy (exit when AI bias flips)</li>
                    <li><strong>Do NOT pyramid</strong> - theta decay makes it mathematically impossible</li>
                    <li>Consider weekly swing as a complement to 0DTE, not a replacement</li>
                </ul>
                <p class="mt-3 mb-0"><strong>For Pyramiding to Work:</strong></p>
                <ul class="mb-0">
                    <li>Use monthly options (30+ DTE) minimum</li>
                    <li>Ideal: Quarterly options (60+ DTE) or LEAPS</li>
                    <li>Or pyramid with underlying shares (no expiration)</li>
                </ul>
            </div>
        </div>

        <!-- Conclusion -->
        <div class="section">
            <h2>Conclusion</h2>

            <div class="metric-cards">
                <div class="metric-card success">
                    <div class="label">0DTE Strategy</div>
                    <div class="value">$34,626</div>
                    <div class="label">BEST CHOICE</div>
                </div>
                <div class="metric-card">
                    <div class="label">Weekly Swing</div>
                    <div class="value">$32,319</div>
                    <div class="label">VIABLE ALTERNATIVE</div>
                </div>
                <div class="metric-card danger">
                    <div class="label">Weekly + Pyramid</div>
                    <div class="value">$8,153</div>
                    <div class="label">AVOID</div>
                </div>
            </div>

            <h3>Key Takeaways</h3>
            <ol>
                <li><strong>Weekly swing trading works</strong> - Produces $32,319 with 55.7% win rate and fewer trades</li>
                <li><strong>Bias-reversal exits are critical</strong> - 68% of weeks have mid-week reversals</li>
                <li><strong>Pyramiding fails with weekly options</strong> - Theta decay acceleration destroys the math</li>
                <li><strong>Time horizon matters</strong> - Pyramiding requires 30+ DTE options or stocks</li>
                <li><strong>0DTE remains optimal</strong> - Highest returns with proven 88.9% AI directional accuracy</li>
            </ol>

            <p class="text-muted mt-4" style="font-size: 0.9rem;">
                <strong>Disclaimer:</strong> Past performance does not guarantee future results. Options trading involves significant risk
                of loss. This analysis is for educational purposes only and should not be considered financial advice.
            </p>
        </div>

        <!-- Footer -->
        <div class="text-center text-muted py-4">
            <p>Generated by Claude Opus 4.5 | Analysis Date: December 2025</p>
            <p>AI Bias Strategy Backtest on QQQ 2025 Data</p>
        </div>
    </div>
`;

const styles = `
    body {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        background-color: #37474f;
        color: #333;
        line-height: 1.6;
    }
    .swing-weekly-page {
        background-color: #37474f;
        min-height: 100vh;
        padding: 20px 0;
    }
    .swing-weekly-page .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 20px;
    }
    .swing-weekly-page .header {
        background: linear-gradient(135deg, #1a237e 0%, #4a148c 100%);
        color: white;
        padding: 40px;
        border-radius: 10px;
        margin-bottom: 30px;
        text-align: center;
    }
    .swing-weekly-page .header h1 {
        margin: 0;
        font-size: 2.5rem;
    }
    .swing-weekly-page .header .subtitle {
        opacity: 0.9;
        font-size: 1.2rem;
        margin-top: 10px;
    }
    .swing-weekly-page .section {
        background: white;
        border-radius: 10px;
        padding: 30px;
        margin-bottom: 25px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.08);
    }
    .swing-weekly-page .section h2 {
        color: #1a237e;
        border-bottom: 3px solid #4a148c;
        padding-bottom: 10px;
        margin-bottom: 20px;
    }
    .swing-weekly-page .section h3 {
        color: #4a148c;
        margin-top: 25px;
        margin-bottom: 15px;
    }
    .swing-weekly-page .metric-cards {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        margin: 20px 0;
    }
    .swing-weekly-page .metric-card {
        flex: 1;
        min-width: 200px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        border-radius: 10px;
        text-align: center;
    }
    .swing-weekly-page .metric-card.success {
        background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    }
    .swing-weekly-page .metric-card.warning {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    }
    .swing-weekly-page .metric-card.danger {
        background: linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%);
    }
    .swing-weekly-page .metric-card .label {
        font-size: 0.9rem;
        opacity: 0.9;
    }
    .swing-weekly-page .metric-card .value {
        font-size: 2rem;
        font-weight: bold;
        margin-top: 5px;
    }
    .swing-weekly-page table {
        width: 100%;
        border-collapse: collapse;
        margin: 20px 0;
    }
    .swing-weekly-page th, .swing-weekly-page td {
        padding: 12px 15px;
        text-align: left;
        border-bottom: 1px solid #ddd;
    }
    .swing-weekly-page th {
        background-color: #1a237e;
        color: white;
    }
    .swing-weekly-page tr:hover {
        background-color: #f5f5f5;
    }
    .swing-weekly-page .highlight-row {
        background-color: #e8f5e9 !important;
        font-weight: bold;
    }
    .swing-weekly-page .negative {
        color: #c62828;
    }
    .swing-weekly-page .positive {
        color: #2e7d32;
    }
    .swing-weekly-page .formula-box {
        background: #263238;
        color: #4fc3f7;
        padding: 20px;
        border-radius: 8px;
        font-family: 'Courier New', monospace;
        margin: 15px 0;
        overflow-x: auto;
    }
    .swing-weekly-page .insight-box {
        background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
        border-left: 4px solid #ff9800;
        padding: 20px;
        margin: 20px 0;
        border-radius: 0 8px 8px 0;
    }
    .swing-weekly-page .warning-box {
        background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
        border-left: 4px solid #f44336;
        padding: 20px;
        margin: 20px 0;
        border-radius: 0 8px 8px 0;
    }
    .swing-weekly-page .success-box {
        background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
        border-left: 4px solid #4caf50;
        padding: 20px;
        margin: 20px 0;
        border-radius: 0 8px 8px 0;
    }
    .swing-weekly-page .comparison-table th {
        background-color: #4a148c;
    }
    .swing-weekly-page .badge-best {
        background-color: #4caf50;
        color: white;
        padding: 3px 8px;
        border-radius: 4px;
        font-size: 0.8rem;
        margin-left: 8px;
    }
    .swing-weekly-page .badge-avoid {
        background-color: #f44336;
        color: white;
        padding: 3px 8px;
        border-radius: 4px;
        font-size: 0.8rem;
        margin-left: 8px;
    }
    .swing-weekly-page .timeline {
        position: relative;
        padding-left: 30px;
    }
    .swing-weekly-page .timeline-item {
        position: relative;
        padding-bottom: 20px;
        border-left: 2px solid #4a148c;
        padding-left: 20px;
    }
    .swing-weekly-page .timeline-item::before {
        content: '';
        position: absolute;
        left: -8px;
        top: 0;
        width: 14px;
        height: 14px;
        background: #4a148c;
        border-radius: 50%;
    }
    .swing-weekly-page .timeline-item.loss::before {
        background: #f44336;
    }
    .swing-weekly-page .timeline-item.profit::before {
        background: #4caf50;
    }
    .access-denied {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: linear-gradient(135deg, #1a237e 0%, #4a148c 100%);
        color: white;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }
    .access-denied h1 {
        font-size: 2rem;
        margin-bottom: 1rem;
    }
    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: linear-gradient(135deg, #1a237e 0%, #4a148c 100%);
        color: white;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }
`;

function SwingWeekly() {
  const [searchParams] = useSearchParams();
  const [isValid, setIsValid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateKey = async () => {
      const key = searchParams.get('key');

      if (!key) {
        setIsValid(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/validate-swing-weekly-key?key=${encodeURIComponent(key)}`);
        const data = await response.json();
        setIsValid(data.valid);
      } catch (error) {
        setIsValid(false);
      }

      setIsLoading(false);
    };

    validateKey();
  }, [searchParams]);

  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading">
          <p>Validating access...</p>
        </div>
      </>
    );
  }

  if (!isValid) {
    return (
      <>
        <style>{styles}</style>
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>Invalid or missing access key.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="swing-weekly-page" dangerouslySetInnerHTML={{ __html: resultsHtml }} />
    </>
  );
}

export default SwingWeekly;
